import { useAuthState } from '@auth/hooks';
import { routes } from '@communities/routes';
import { FEATURE_FLAGS, UserRoles } from '@core/domain';
import { useIsFeatureEnabled, useUserPreferences } from '@core/hooks';
import { routes as auditRoutes } from '@internal-audit/routes';
import {
  Icon,
  IconSize,
  ChevronLeft,
  Search,
  FolderFindContent,
  TestBeaker,
  ServerProcesses,
  Folder,
  Complaint,
  Details,
  Flag,
  Favorite,
  FavoriteRemove,
} from '@soteria/design-system-ve';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { config } from '../../../config';

import { CommunityImage } from './CommunityImage';
import * as styles from './Guide.css';
import type { GuideCommunity } from './useCommunityGroupsWithCommunities';

interface GuideCommunityPagesProps {
  community: GuideCommunity;
  communityGroupId: string | null;
  onBack: () => void;
  onClose: () => void;
}

/**
 * GuideCommunityPages component
 * Displays available pages for a selected community
 * Xbox-inspired compact navigation
 *
 * Authorization based on source code audit:
 * - Content Search & Classification Testing: All authenticated users (no restrictions)
 * - Audits: QA role only (CommunityAuditGuard enforces this)
 * - Community Details: All authenticated users (no restrictions)
 * - Community Group & Synesis: Admin role required
 */

export function GuideCommunityPages({ community, communityGroupId, onBack, onClose }: GuideCommunityPagesProps) {
  const navigate = useNavigate();
  const { hasRoleAccess } = useAuthState();
  const isCaseSearchEnabled = useIsFeatureEnabled(FEATURE_FLAGS.CASE_SEARCH);
  const { addFavorite, removeFavorite, isFavorite } = useUserPreferences();

  // Authorization checks based on actual page requirements from source code audit
  const hasQAAccess = hasRoleAccess(UserRoles.QA, communityGroupId ?? undefined, community.id);
  const hasAdminAccess = hasRoleAccess(UserRoles.ADMIN, communityGroupId ?? undefined, community.id);
  const hasModerationAccess = hasRoleAccess(UserRoles.MODERATOR, communityGroupId ?? undefined, community.id) || hasAdminAccess;

  // Build list of available pages with their routes
  const pages = useMemo(() => {
    const pageList: Array<{
      id: string;
      label: string;
      path: string;
      icon: typeof Details;
      visible: boolean;
    }> = [
      {
        id: `${community.id}-details`,
        label: 'Community Details',
        path: routes.communities.detail(community.id),
        icon: Details,
        visible: true,
      },
      {
        id: `${community.id}-content-search`,
        label: 'Content Search',
        path: routes.communities.contentSearch.root(community.id),
        icon: Search,
        visible: true,
      },
      {
        id: `${community.id}-case-search`,
        label: 'Case Search',
        path: routes.communities.caseSearch.root(community.id),
        icon: FolderFindContent,
        visible: isCaseSearchEnabled && hasModerationAccess,
      },
      {
        id: `${community.id}-audits`,
        label: 'Audits',
        path: auditRoutes.internalAudit.community.audits(community.id),
        icon: Flag,
        visible: hasQAAccess,
      },
      {
        id: `${community.id}-moderation`,
        label: 'Moderation',
        path: `/communities/${community.id}/moderation`,
        icon: Complaint,
        visible: hasModerationAccess,
      },
      {
        id: `${community.id}-classification-testing`,
        label: 'Classification Testing',
        path: routes.communities.classificationTesting.root(community.id),
        icon: TestBeaker,
        visible: true,
      },
      {
        id: `${communityGroupId}-community-group`,
        label: 'Community Group',
        path: communityGroupId ? routes.communityGroups.detail(communityGroupId) : '',
        icon: Folder,
        visible: Boolean(communityGroupId) && hasAdminAccess,
      },
    ];

    return pageList.filter(page => page.visible);
  }, [community.id, communityGroupId, hasQAAccess, hasAdminAccess, hasModerationAccess, isCaseSearchEnabled]);

  const handlePageClick = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleSynesisClick = () => {
    const synesisUrl = config.externalServices.synesisUrl;

    if (synesisUrl) {
      // Open in new tab
      window.open(synesisUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCommunityHeaderClick = () => {
    navigate(routes.communities.detail(community.id));
    onClose();
  };

  // Handle toggling favorite status for a page
  const handleToggleFavorite = useCallback(
    (page: { id: string; label: string; path: string }) => {
      const favoriteId = page.id;

      if (isFavorite(favoriteId)) {
        removeFavorite(favoriteId);
      } else {
        addFavorite({
          id: favoriteId,
          type: 'page',
          label: `${community.name} - ${page.label}`,
          route: page.path,
          communityId: community.id,
        });
      }
    },
    [addFavorite, removeFavorite, isFavorite, community.id, community.name]
  );

  return (
    <div className={styles.pagesList}>
      {/* Back button */}
      <button aria-label="Back to communities" className={styles.pageButton} type="button" onClick={onBack}>
        <Icon className={styles.pageIcon} icon={ChevronLeft} iconSize={IconSize.Small} />
        <span>Back</span>
      </button>

      {/* Community header - clickable to navigate to community details */}
      <button aria-label={`Navigate to ${community.name} details`} className={styles.communityHeaderButton} type="button" onClick={handleCommunityHeaderClick}>
        <CommunityImage communityName={community.name} fetchPriority="high" loading="eager" logoUrl={community.logoUrl} />
        <div className={styles.communityHeaderContent}>
          <div className={styles.communityHeaderTitle}>{community.name}</div>
        </div>
      </button>

      {/* Page items with favorite action */}
      {pages.map(page => {
        const isPageFavorite = isFavorite(page.id);

        return (
          <div key={page.id} className={styles.pageItemContainer}>
            <button aria-label={`Navigate to ${page.label}`} className={styles.pageItemNavButton} type="button" onClick={() => handlePageClick(page.path)}>
              <Icon className={styles.pageIcon} icon={page.icon} iconSize={IconSize.Small} />
              <span>{page.label}</span>
            </button>
            <button
              aria-label={isPageFavorite ? `Remove ${page.label} from favorites` : `Add ${page.label} to favorites`}
              aria-pressed={isPageFavorite}
              className={styles.pageItemHomeButton}
              data-is-favorite={isPageFavorite}
              title={isPageFavorite ? 'Remove from favorites' : 'Add to favorites'}
              type="button"
              onClick={() => handleToggleFavorite(page)}
            >
              <Icon icon={isPageFavorite ? FavoriteRemove : Favorite} iconSize={IconSize.XSmall} />
            </button>
          </div>
        );
      })}

      {/* Synesis - Data & Analytics - Requires admin access (external link, no favorite option) */}
      {config.externalServices.synesisUrl && hasAdminAccess && (
        <button aria-label="Navigate to Synesis - Data & Analytics" className={styles.pageButton} type="button" onClick={handleSynesisClick}>
          <Icon className={styles.pageIcon} icon={ServerProcesses} iconSize={IconSize.Small} />
          <span>Synesis - Data & Analytics</span>
        </button>
      )}
    </div>
  );
}
